/**
 * Success_Handler.ts (ported from SK Catalog's src/utils/successHandler.ts)
 *
 * Single source of truth for success toast messages. Always prefers the REAL
 * backend success message; falls back to a supplied string.
 *
 * Backend success response shapes handled:
 *   { message: string }            — most APIs
 *   { data: { message: string } }  — wrapped envelope responses
 *
 * Usage:
 *   showSuccess(res, 'Saved');                 // backend message as subtitle
 *   showSuccess(res, 'Saved', 'Fallback…');    // custom fallback subtitle
 *   getSuccessMessage(res, 'Fallback');        // pure extractor, no toast
 */
import { showToast } from '../components/toastComponent';

const DEFAULT_FALLBACK = 'Operation completed successfully.';

/** Pure extractor — mirrors getApiErrorMessage. */
export function getSuccessMessage(response: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!response || typeof response !== 'object') return fallback;
  const res = response as any;
  if (typeof res.message === 'string' && res.message.trim()) return res.message.trim();
  if (typeof res.data?.message === 'string' && res.data.message.trim()) return res.data.message.trim();
  return fallback;
}

/**
 * Shows a success toast.
 *
 * @param response - raw backend response object
 * @param title    - toast heading (e.g. 'Saved', 'Booked')
 * @param fallback - subtitle shown when the backend sends no message
 */
export function showSuccess(response: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  showToast('success', title, getSuccessMessage(response, fallback));
}
